import { ElementRef, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from 'src/environment/environment';
import * as CryptoJS from 'crypto-js';
import * as Highcharts from 'highcharts';

export interface ModuleItem {
  _id: string;
  name: string;
  moduleSlug: string;
  permission: boolean;
}

interface Item {
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class SharedService {

  public allMeasurements: any[] = [];
  private selectedNodeSensors: Map<string, any[]> = new Map();
  readonly PORTAL_CONFIG: string = 'portalConfig';
  readonly INSTALLATION_CONFIG: string = 'installationData';
  readonly ENCRYPTION_KEY = '4bX9z3vQN2WcE7YpJgHdLrMfTuXsaK1!';
  readonly IV_LENGTH = 16;

  public isActive: boolean = false;
  public sidebarWidth: string = '';
  public contentWidth: string = '';
  public sidebarTogglerRight: string = '-1.4vw';
  public shouldHideToggleButton: boolean = false;
  public userStore: { [key: string]: any } = {};

  private chartStats: any[] = [];
  private compareStats: any[] = [];
  private chartSeries: any[] = [];
  private compareSeries: any[] = [];
  private updateChartInterval: any;
  private updateComareInterval: any;
  private updateChartFold: any;
  private updateCompareFold: any;
  private userCredentials: any = null
  public toggleTreeActive: boolean = false;

  private measurementSubject = new BehaviorSubject<any>(null);
  measurement$ = this.measurementSubject.asObservable();

  private dataToSendInfo = new BehaviorSubject<any>(null);

  private sensorInfoSubject = new BehaviorSubject<any[]>([]);
  public sensorInfo$ = this.sensorInfoSubject.asObservable();

  private roleUpdatedSource = new Subject<void>();
  roleUpdated$ = this.roleUpdatedSource.asObservable();

  public userPermissions = new BehaviorSubject<any>([]);
  userPermissions$ = this.userPermissions.asObservable();

  public poralConfiguration$ = new BehaviorSubject<any>({});
  public toggleClearButton$ = new BehaviorSubject<boolean>(false);

  private readonly STORAGE_KEYS: string[] = [
    'username',
    'email',
    'permissionlevel',
    'timeZone',
    'otpAttempts',
    'treeNodeLabel',
    'groupNodeLabel',
    'masterNodeLabel',
    'roleRank',
    'roleId',
    'interval',
    'fold',
    'customerId',
    'viewMode',
    'portalTheme'
  ];

  constructor() {
    this.initializePortalConfig();
  }

  private initializePortalConfig(): void {
    this.STORAGE_KEYS.forEach((key) => {
      const stored = sessionStorage.getItem(key);
      if (stored !== null) {
        try {
          this.userStore[key] = JSON.parse(stored);
        } catch {
          this.userStore[key] = stored;
        }
      }
    });

    this.poralConfiguration$.next(this.userStore);
  }

  setModuleList(module: any []):void {
    this.userPermissions.next(module);
  }

  public setUserProperty(key: string, value: any): void {
    this.userStore[key] = value;
    this.poralConfiguration$.next(this.userStore);
    sessionStorage.setItem(key,value);
  }

  setPortalConfiguration(credentials: any) {
    this.userCredentials = credentials;
    sessionStorage.setItem(this.PORTAL_CONFIG, JSON.stringify(credentials));
  }

  getPortalConfiguration() {
    if (!this.userCredentials) {
      const storedCredentials = sessionStorage.getItem(this.PORTAL_CONFIG);
      if (storedCredentials)
        this.userCredentials = JSON.parse(storedCredentials);
    }
    return this.userCredentials;
  }

 getUserPermissions(): any[] {
    const creds = this.getPortalConfiguration();
    const permissions = creds?.permissions ?? [];
    return permissions
  }

  hasPermission(slug: string): boolean {
    const userPermissions: any = this.transFromSlugs(this.getUserPermissions());
    return userPermissions.includes(slug);
  }

  private transFromSlugs(permissions: any): string [] {
    const result: string[] = [];
    permissions
    .forEach((item: any) => {
      if (item?.moduleSlug) {
        result.push(item.moduleSlug);
      }
      const subModuleList = Array.isArray(item.subModules) ? item.subModules.map((subItem: any) => subItem.moduleSlug) : [];
      subModuleList.forEach((subSlug: string) => {
        if (subSlug) result.push(subSlug);
      });
    });

    return result;
  }

  get(key: string): any {
    if (this.userStore[key] !== undefined) {
      return this.userStore[key];
    }

    const stored = sessionStorage.getItem(key);
    if (stored !== null) {
      this.userStore[key] = stored;
      return stored;
    }

    return null;
  }

  toggleTree() {
    this.toggleTreeActive = !this.toggleTreeActive;
  }

  toggleMap() {
    this.isActive = !this.isActive;
    this.updateStyles();
  }

  updateStyles() {
    if (window.matchMedia('(min-width: 990px)').matches) {
      if (this.isActive) {
        this.sidebarWidth = '0%';
        this.contentWidth = '100%';
        this.sidebarTogglerRight = '-1.4vw';
      } else {
        this.sidebarWidth = '21%';
        this.contentWidth = '79%';
        this.sidebarTogglerRight = '-1.4vw';
      }
      this.shouldHideToggleButton = false;
    } else {
      this.sidebarWidth = '100%';
      this.contentWidth = '100%';
      this.sidebarTogglerRight = '0';
      this.shouldHideToggleButton = true;
    }
  }

  toggleClear(status: boolean){
    this.toggleClearButton$.next(status);
  }

  inArray<T extends Item>(
    array: T[],
    key: keyof T,
    value: T[keyof T]
  ): boolean {
    return array.some((item) => item[key] === value);
  }

  changeMeasurement(measurement: any) {
    this.measurementSubject.next(measurement);
  }

  getInfoSensors(): any[] {
    return this.sensorInfoSubject.getValue();
  }

  setInfoSensors(data: any[]): void {
    this.sensorInfoSubject.next(data);
  }

  clearSensorInfo(): void {
    this.sensorInfoSubject.next([]);
  }

  setDataToSend(data: any): void {
    this.dataToSendInfo.next(data);
  }

  clearDataInfoWindow(): any {
    this.dataToSendInfo.next(null);
  }

  getDataToSend() {
    return this.dataToSendInfo.getValue();
  }

  setChartStats(stats: any[]): void {
    this.chartStats = stats;
  }

  getChartStats(): any[] {
    return this.chartStats;
  }

  setChartSeries(series: any): void {
    this.chartSeries = series;
  }

  getChartSeries(): any[] {
    return this.chartSeries;
  }

  setCompareSeries(series: any): void {
    this.compareSeries = series;
  }

  getCompareSeries(): any[] {
    return this.compareSeries;
  }

  setComparisonStats(series: any): void {
    this.compareStats = series;
  }

  getComparisonStats(): any[] {
    return this.compareStats;
  }

  setChartInterVal(interval: any): void {
    this.updateChartInterval = interval;
  }
  getChartInterVal(): any[] {
    return this.updateChartInterval;
  }

  setCompareInterVal(interval: any): void {
    this.updateComareInterval = interval;
  }
  getCompareInterVal(): any[] {
    return this.updateComareInterval;
  }

  setChartFold(fold: any): void {
    this.updateChartFold = fold;
  }

  getChartFold(): any[] {
    return this.updateChartFold;
  }

  setCompareFold(fold: any): void {
    this.updateCompareFold = fold;
  }

  getCompareFold(): any[] {
    return this.updateCompareFold;
  }

  closeModal(modal: ElementRef) {
    $(modal.nativeElement).modal('hide');
    $('.modal-backdrop').remove();
  }

  openModal(modal: ElementRef) {
    $(modal.nativeElement).modal('show');
  }

  setNodeSensors(nodeId: string, sensors: any[], moduleId?: any): void {
    if (this.selectedNodeSensors.has(nodeId)) return;
    const clonedSensors = sensors.map(s => ({
      ...s,
      __nodeId: nodeId   // 🔑 track origin node
    }));
    this.selectedNodeSensors.set(nodeId, clonedSensors);
    this.refreshMeasurements(moduleId);
  }

  unsetNodeSensors(nodeId: string, moduleId?: any): void {
    this.selectedNodeSensors.delete(nodeId);
    this.refreshMeasurements(moduleId);
  }

  unsetAllNodeSensors(currentModuleId?: any): void {
    this.selectedNodeSensors.clear();
    this.allMeasurements = [];
    this.refreshMeasurements(currentModuleId);
  }

  getMeasurements(): any[] {
    return this.allMeasurements;
  }

  private refreshMeasurements(currentModuleId?: string): void {
    if (this.selectedNodeSensors.size === 0) {
      this.allMeasurements = [];
      return;
    }

    const mergedSensors: any[] = [];
    this.selectedNodeSensors.forEach(list => mergedSensors.push(...list));

    const sensorMap = new Map<string, any>();

    mergedSensors.forEach(sensor => {
      if (
        currentModuleId &&
        !sensor.visibleModules?.includes(currentModuleId)
      ) {
        return;
      }

      const sensorKey = sensor.isWaterQuality
        ? `${sensor.sensorSlug}_${sensor.displayName}`
        : sensor.sensorSlug;
      if (!sensorMap.has(sensorKey)) {
        sensorMap.set(sensorKey, sensor);
      }
    });

    this.allMeasurements = Array.from(sensorMap.values());
  }

  chartResolution() {
    const fontSizes = {
      small: '10px',
      medium: '12px',
      large: '14px',
      xLarge: '28px',
      xxLarge: '40px',
      xxxLarge: '40px',
    };

    const widths = {
      large: '60px',
      xLarge: '60px',
      xxLarge: '80px',
    };

    const screenWidth = window.innerWidth;

    const resolutionMap = [
      { maxWidth: 600, fontSize: fontSizes.small, largeWidth: widths.large },
      { maxWidth: 1024, fontSize: fontSizes.medium, largeWidth: widths.large },
      { maxWidth: 1920, fontSize: fontSizes.large, largeWidth: widths.large },
      { maxWidth: 2560, fontSize: fontSizes.xLarge, largeWidth: widths.xLarge },
      {
        maxWidth: 3840,
        fontSize: fontSizes.xxLarge,
        largeWidth: widths.xxLarge,
      },
      {
        maxWidth: Infinity,
        fontSize: fontSizes.xxxLarge,
        largeWidth: widths.xxLarge,
      },
    ];

    const { fontSize, largeWidth } = resolutionMap.find(
      (res) => screenWidth <= res.maxWidth
    )!;

    return { fontSize, largeWidth };
  }

  notifyRoleChange() {
    this.roleUpdatedSource.next();
  }

  filterRolesByUser(roles: { _id: string; name: string; rank: number }[], roleRank: number) {
    const existRank = typeof roleRank === 'string' ? Number(roleRank) : roleRank;
    if(!roleRank) return roles;
    return roles.filter(role => role.rank >= existRank).sort((a, b) => a.rank - b.rank);
  }

  getColor(alarmType: string): string {
    if (
      alarmType === 'Lower Target Criteria' ||
      alarmType === 'Upper Target Criteria'
    ) {
      return '#fbbc05';
    } else if (
      alarmType === 'Lower Critical Limit' ||
      alarmType === 'Upper Critical Limit'
    ) {
      return '#ea4335';
    } else {
      return 'inherit';
    }
  }

  getCurrentRouteSegment(router: Router): string {
    const segments = router.url.split('/');
    return segments[segments.length - 1];
  }
  getBaseRoute(url: any): string {
    const path = url.split('?')[0];       // remove query params
    const segments = path.split('/');
    return segments[1] || '';
  }

  encryption(text: string): string {
    const iv = CryptoJS.lib.WordArray.random(this.IV_LENGTH);
    const encrypted = CryptoJS.AES.encrypt(
      text,
      CryptoJS.enc.Utf8.parse(this.ENCRYPTION_KEY),
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );

    return (
      iv.toString(CryptoJS.enc.Hex) +
      ':' +
      encrypted.ciphertext.toString(CryptoJS.enc.Hex)
    );
  }

  getCurrentModule(url: any): any {
    const cleanUrl = url.split('?')[0].split('#')[0];
    const segments = cleanUrl.split('/').filter(Boolean);
    const moduleSlug = segments.length > 1 ? segments[1] : segments[0] || '';
    const permissions = this.getUserPermissions?.() || [];

    const findModule = permissions.find(m => m.moduleSlug === moduleSlug);
    if (findModule) {
      return { moduleId: findModule?.id ?? findModule?._id, selectedNode: findModule.defaultTreeNodeId };
    }
    return { moduleId: '', selectedNode: '' };
  }

  getMarkerIcon(productModel: any): string {
    const basePath = '../../../assets/img/';
    const defaultMarker = 'marker1.png';
    const allowedExtensions = ['png', 'jpg', 'jpeg', 'svg'];
    let marker = productModel?.markerIcon || defaultMarker;
    const hasExtension = allowedExtensions.some(ext => marker.toLowerCase().endsWith(`.${ext}`));

    if (!hasExtension) {
      marker = `${marker}.png`;
    }

    return basePath + marker;
  }
}
